import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Translations {
  [key: string]: any;
}

interface TranslationResources {
  en: Translations;
  ro: Translations;
}

const translations: TranslationResources = {
  en: require('./constants/dictionary/en/translation.json'),
  ro: require('./constants/dictionary/ro/translation.json'),
};

export type Language = keyof TranslationResources;

interface TranslationContextProps {
  language: Language;
  switchLanguage: (lng: Language) => void;
  t: (key: string) => string;
}

const TranslationContext = createContext<TranslationContextProps>({
  language: 'en',
  switchLanguage: () => {},
  t: (key: string) => key,
});

interface TranslationProviderProps {
  children: ReactNode;
}

const getNestedTranslation = (obj: any, key: string): string | undefined => {
  return key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
};

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage =
      (localStorage.getItem('language') as Language) ||
      (navigator.language.split('-')[0] as Language) ||
      'en';
    console.log('Initial language loaded:', savedLanguage);
    setLanguage(savedLanguage);
  }, []);

  const switchLanguage = (lng: Language) => {
    console.log('Switching language in context to:', lng);
    setLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const t = (key: string): string => {
    const translation = getNestedTranslation(translations[language], key);
    if (!translation) {
      console.warn(`Missing translation for key: ${key} in language: ${language}`);
    }
    return translation || key;
  };

  return (
    <TranslationContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
